// @flow
import type { LeafRuleProps } from 'types/rule-types';

import _ from 'lodash';
import React, { Component } from 'react';
import { Dropdown, Form, Popup } from 'semantic-grab-ui/src';
import { Select } from 'grab-ui';
import {
  operatorOptions,
  getOperatorDisp,
} from 'omega-rtc/components/rule-builder/utils';
import CDatePicker from 'components/custom/CDatePicker';
import moment from 'moment';

const BOOL_OPTIONS = [
  { key: 1, text: 'True', value: 'true' },
  { key: 0, text: 'False', value: 'false' },
];

// const DEFAULT_VALIDATION_ERROR = 'This rule is not valid';
const INPUT_VALIDATION_ERROR = 'All the values entered should be numbers';
const CUSTOM_MULTIPLE_DISPLAY = true;
const { Option } = Select;

export default class LeafRule extends Component {
  props: LeafRuleProps;

  constructor(props: LeafRuleProps) {
    super(props);
    this.state = {
      rhs: props.rule.rhs,
    };
  }

  handleRuleOperatorSelect = (event: Object, dropdown: Object) => {
    const { rule } = this.props;
    const newRule = Object.assign({}, rule, { operator: dropdown.value });
    this.props.onRuleChange(newRule);
  }

  handleRhsChangeInput = (event: Object, data: Object) => {
    const { rule, ruleType } = this.props;
    let value = event.target.value || data.value;
    value = value.toString();
    let values = value.split(',').map(v => v.trim());
    values = values.filter(v => v !== '');

    let validationError = '';

    // If the ruletype objecttype is numeric and the values are not all numbers set an error
    if (ruleType.objectType === 'number') {
      const parsedValues = values.map(v => parseFloat(v, 10));
      validationError = (_.some(parsedValues, v => isNaN(v)) && INPUT_VALIDATION_ERROR) || validationError;
      if (!validationError) {
        parsedValues.forEach((parsedValue) => {
          if (parsedValue >= Number.MAX_SAFE_INTEGER) {
            validationError = `value should be lesser than ${Number.MAX_SAFE_INTEGER}`;
          }
        });
        if (!validationError) values = parsedValues;
      }
    }

    // Remove array for singular values
    if (values.length === 1 && !['ixn', '!ixn', 'in', '!in', 're'].includes(rule.operator)) {
      values = values[0];
    }

    //   // Validate value for eq operator
    // if (values.length > 1 && ['eq', 'ne'].includes(rule.operator)) {
    //   validationError = "'comma' is not allowed. Use 'in' operator for condition with multiple values"
    //   values = values[0];
    // }

    // Check JSON string length
    if (JSON.stringify(values).length > 4096) {
      validationError = 'Rule value length cannot exceed 4096';
    }

    // Reset input to original value after blur
    if (validationError) {
      this.setState({ rhs: this.props.rule.rhs });
    }

    const newRule = Object.assign({}, rule, { rhs: values });
    this.props.onRuleChange(newRule, validationError);
  }

  handleRhsChangeBool = (event: Object, dropdown: Object) => {
    const { rule } = this.props;
    const newRule = Object.assign({}, rule, { rhs: dropdown.value === 'true' });
    this.props.onRuleChange(newRule);
  }

  handleRhsChangeDate = (event: Object, data: {name: string, value: Moment}) => {
    const { rule } = this.props;
    const newRule = Object.assign({}, rule, {
      rhs: data.value ? data.value.unix() : null,
    });
    this.props.onRuleChange(newRule);
  }

  render() {
    const { rule, ruleType, isSelected } = this.props;
    if (_.isUndefined(rule)) return null;

    if (_.isUndefined(ruleType)) {
      return (<span>`${rule.lhs} is not found in ruleTypes`</span>);
    }

    return (
      <span>
        <span>{ruleType.noun}</span>
        <span className="horizontal-margin">
          <Dropdown
            inline
            trigger={<span className="rule-field">{getOperatorDisp(rule.operator)}</span>}
            options={operatorOptions(ruleType)}
            defaultValue={rule.operator}
            onChange={this.handleRuleOperatorSelect}
            disabled={!isSelected}
          />
        </span>
        {this.renderRhs()}
      </span>
    );
  }

  renderRhs() {
    const { rule, ruleType, isSelected } = this.props;
    const { options = [] } = ruleType;
    const selectedOption = _.get(_.find(options, ['value', rule.rhs]), 'name');
    const isMultipleSupport = ['ixn', '!ixn', 'in', '!in'].includes(rule.operator);

    // Boolean Type
    if (ruleType && ruleType.objectType === 'bool') {
      const value = rule.rhs === false ? 'false' : 'true';
      return (
        <Dropdown
          inline
          trigger={<span className="rule-field">{_.get(_.find(BOOL_OPTIONS, { value }), 'text')}</span>}
          options={BOOL_OPTIONS}
          value={value}
          onChange={this.handleRhsChangeBool}
          disabled={!isSelected}
        />
      );
    }

    // Date Type with objectType is number of unix
    if (ruleType && ruleType.objectType === 'number' && ruleType.displayType === 'datetime') {
      const value = _.get(rule, 'rhs');
      const mDate = value ? moment.unix(value) : moment();
      return (
        <CDatePicker
          required
          selectsStart
          showTimeSelect
          selected={mDate}
          startDate={mDate}
          disabled={!isSelected}
          handleInputChange={this.handleRhsChangeDate}
        />
      );
    }

    // Default Type
    if (this.props.isSelected) {
      if (!_.isEmpty(options)) {
        const rhsValue = _.get(rule, 'rhs');
        if (isMultipleSupport) {
          const slOptions = _.map(options, opt => {
            return <Option value={opt.value} title={opt.name}>{opt.name}</Option>;
          });

          return (
            <Select
              value={rhsValue || []}
              style={{ width: 200 }}
              mode="multiple"
              optionFilterProp="title"
              onChange={(value) => {
                this.handleRhsChangeInput({ target: { value } });
              }}
            >
              {slOptions}
            </Select>
          );
        }
        return (
          <Form.Select
            name={`${rule.uid}-select`}
            options={_.map(options, option => ({
              ...option,
              key: option.value,
              text: option.name,
            }))}
            value={rhsValue}
            onChange={this.handleRhsChangeInput}
          />
        );
      }

      const defaultInput = (<Form.Input
        name={`${rule.uid}-input`}
        value={this.state.rhs}
        onChange={(e) => this.setState({ rhs: e.target.value })}
        onBlur={this.handleRhsChangeInput}
      />);

      return (
        _.get(ruleType, 'objectType') === 'string' ?
          <Popup
            content="The input is case sensitive"
            trigger={
              defaultInput
            }
          />
          :
          defaultInput
      );
    }

    if (isMultipleSupport && !_.isEmpty(options) && CUSTOM_MULTIPLE_DISPLAY) {
      return getMultipleDisplayText(rule, options);
    }

    return rule.rhs || rule.rhs === 0 ?
      <span className="rule-field">{_.isArray(rule.rhs) ? rule.rhs.join(', ') : `${selectedOption || rule.rhs}`}</span> :
      <span>Value(s)</span>;
  }
}

function getMultipleDisplayText(rule, options) {
  const rhsValue = _.get(rule, 'rhs');
  const mapping = {};
  if (rhsValue.length) {
    _.forEach(options, item => {
      mapping[item.value] = item.name;
    });
    return _.map(rule.rhs, rhsValue => {
      return <span style={{ display: 'block' }}>{mapping[rhsValue]}</span>;
    });
  }
}
